// Header.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import ResponsiveImage from './ResponsiveImage';
import { getImageUrl } from '../lib/supabase';

function Header() {
  const [isDesktop, setIsDesktop] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const subheadingText = "Za svaki Vaš korak".split(" ");

  useEffect(() => {
    setIsDesktop(window.innerWidth >= 992);
  }, []);

  return (
    <motion.header
      className="header-section position-relative overflow-hidden"
      style={{ color: '#495057', minHeight: '100vh', backgroundColor: '#dbc2ac' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1, ease: 'easeOut' }}
    >
      {/* Preload removed — ResponsiveImage handles srcset automatically */}

      <motion.div
        className="position-absolute top-0 start-0 w-100 h-100"
        style={{ zIndex: 2 }}
        initial={{ opacity: 0, scale: 1.05 }}
        animate={{ opacity: imageLoaded ? 1 : 0, scale: 1 }}
        transition={{ duration: 1.5, ease: 'easeOut' }}
      >
        <ResponsiveImage
          className="header-bg w-100 h-100"
          src={getImageUrl('akrion-07.webp')}
          sizes="100vw"
          alt="Masthead Background"
          loading="eager"
          fetchPriority="high"
          onLoad={() => setImageLoaded(true)}
          objectFit="cover"
        />
      </motion.div>

      <div className="header-content container position-relative d-flex align-items-center justify-content-center" style={{ minHeight: '100vh', zIndex: 3 }}>
        <motion.div
          className="header-text text-center p-5"
          style={{
            backgroundColor: 'rgba(219, 194, 172, 0.48)',
            backdropFilter: 'blur(3px)',
            WebkitBackdropFilter: 'blur(3px)',
            borderRadius: '2rem'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut', delay: 1.5 }}
        >
          {isDesktop ? (
            <>
              <motion.div
                className="header-subtitle mb-4"
                style={{
                  color: 'rgb(255, 255, 255)', fontSize: '1.8rem', fontWeight: '600',
                  letterSpacing: '0.5px',
                  textShadow: '0 4px 12px rgba(0, 0, 0, 0.8), 0 2px 6px rgba(0, 0, 0, 0.6)'
                }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut', delay: 0.5 }}
              >
                {subheadingText.map((word, index) => (
                  <motion.span
                    key={index}
                    initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
                    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                    transition={{ duration: 0.8, ease: 'easeOut', delay: 0.7 + (index * 0.2) }}
                    style={{ display: "inline-block", marginRight: "0.4em" }}
                  >
                    {word}
                  </motion.span>
                ))}
              </motion.div>

              <motion.div
                className="header-title mb-4"
                style={{
                  color: '#4259A8', fontWeight: '600', fontSize: 'clamp(2.5rem, 6vw, 4rem)',
                  background: 'linear-gradient(135deg, #4259A8 0%, #2E8DD7 100%)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text', filter: 'drop-shadow(rgba(240, 208, 208, 0.7) 5px 5px 11px)'
                }}
                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1, ease: 'easeOut', delay: 1.5 }}
              >
                Poliklinika Akrion
              </motion.div>

              <motion.div
                className="mx-auto mb-5"
                style={{ width: '120px', height: '4px', background: 'linear-gradient(90deg, #4259A8, #2E8DD7)', borderRadius: '2px' }}
                initial={{ width: 0 }}
                animate={{ width: '120px' }}
                transition={{ duration: 1, ease: 'easeOut', delay: 2.2 }}
              />

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut', delay: 2.5 }}
              >
                <a
                  href="/#contact"
                  className="btn px-5 py-3 rounded-pill fw-bold text-uppercase position-relative overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, #4259A8 0%, #2E8DD7 100%)',
                    border: 'none', color: 'white', fontSize: '1.1rem',
                    boxShadow: '0 8px 25px rgba(66, 89, 168, 0.3)',
                    transition: 'all 0.4s ease', letterSpacing: '1px', textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => { e.target.style.transform = 'translateY(-3px)'; e.target.style.boxShadow = '0 12px 35px rgba(66, 89, 168, 0.4)'; }}
                  onMouseLeave={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 8px 25px rgba(66, 89, 168, 0.3)'; }}
                >
                  Rezerviraj pregled
                </a>
              </motion.div>
            </>
          ) : (
            <>
              <motion.div
                className="header-subtitle mb-4"
                style={{
                  color: 'rgb(255, 255, 255)', fontSize: '1.2rem', fontWeight: '600',
                  letterSpacing: '0.5px', filter: 'drop-shadow(12px 12px 38px rgba(59,54,54,0.77))'
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: 'easeOut', delay: 0.5 }}
              >
                {subheadingText.join(" ")}
              </motion.div>

              <motion.div
                className="header-title mb-4"
                style={{
                  color: '#4259A8', fontWeight: '600', fontSize: 'clamp(2rem, 8vw, 3rem)',
                  letterSpacing: '1px', background: 'linear-gradient(135deg, #4259A8 0%, #2E8DD7 100%)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text', filter: 'drop-shadow(12px 12px 38px rgba(59,54,54,0.77))'
                }}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, ease: 'easeOut', delay: 0.8 }}
              >
                Poliklinika Akrion
              </motion.div>

              <motion.div
                className="mx-auto mb-5"
                style={{ width: '100px', height: '3px', background: 'linear-gradient(90deg, #4259A8, #2E8DD7)', borderRadius: '2px' }}
                initial={{ width: 0 }}
                animate={{ width: '100px' }}
                transition={{ duration: 1, ease: 'easeOut', delay: 1.3 }}
              />

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut', delay: 1.6 }}
              >
                <a
                  href="/#contact"
                  className="btn px-4 py-3 rounded-pill fw-bold text-uppercase"
                  style={{
                    background: 'linear-gradient(135deg, #4259A8 0%, #2E8DD7 100%)',
                    border: 'none', color: 'white', fontSize: '1rem',
                    boxShadow: '0 6px 20px rgba(66, 89, 168, 0.3)',
                    letterSpacing: '1px', textDecoration: 'none'
                  }}
                >
                  Rezerviraj pregled
                </a>
              </motion.div>
            </>
          )}
        </motion.div>
      </div>
    </motion.header>
  );
}

export default Header;